import { Link, useParams } from 'react-router-dom';
import { useCallback, useEffect, useState } from 'react';
import { SidebarLayout } from '../Components/Catalyst/sidebar-layout';
import { Heading } from '../Components/Catalyst/heading';
import { Divider } from '../Components/Catalyst/divider';
import { memberNotificationService } from '../clients/ApiClients';
import { NotificationResponse } from '../clients/rust-api-client';
import { ErrorAlert } from '../Components/Custom/ErrorAlert';
import { ErrorPage } from '../Components/Custom/ErrorPage';
import { apiErrorMessage, apiErrorStatus } from '../utils/apiErrors';
import LoadingSpinner from '../LoadingSpinner';
import { Navbar } from '../Components/Catalyst/navbar';
import AppSidebar from '../AppSidebar';
import {
  BellIcon,
  ExclamationTriangleIcon,
  InformationCircleIcon,
  CheckCircleIcon,
} from '@heroicons/react/24/outline';

const NotificationDetailPage: React.FC = () => {
  const { notificationId } = useParams<{
    notificationId: string;
  }>();
  const [loading, setLoading] = useState(true);
  const [notification, setNotification] = useState<NotificationResponse | null>(
    null
  );
  // A failed load leaves nothing to show and gets the whole page; a
  // failed mark-as-read sits beside a notification that did load.
  const [loadError, setLoadError] = useState<string | null>(null);
  const [markReadError, setMarkReadError] = useState<string | null>(null);

  const fetchNotification = useCallback(async () => {
    if (!notificationId) {
      setLoading(false);
      return;
    }
    let loaded: NotificationResponse;
    try {
      setLoading(true);
      setLoadError(null);
      // Ids are prefixed strings (`notif_…`); the old `Number(id)`
      // sent NaN and could never have matched anything.
      const response = await memberNotificationService.getMyNotification({
        notificationId,
      });
      loaded = response.data;
      setNotification(loaded);
    } catch (err) {
      console.error('Error fetching notification:', err);
      setNotification(null);
      // Someone else's notification answers 404, never 403 — the id
      // space is not probeable, so it reads as missing.
      setLoadError(
        apiErrorStatus(err) === 404
          ? "We couldn't find that notification. It may have been removed."
          : apiErrorMessage(err, "Couldn't load this notification.")
      );
      setLoading(false);
      return;
    }
    setLoading(false);

    // Mark as read when viewing
    if (!loaded.is_read) {
      try {
        await memberNotificationService.markMyNotificationRead({
          notificationId,
        });
        setNotification((prev) => (prev ? { ...prev, is_read: true } : null));
      } catch (err) {
        console.error('Error marking notification read:', err);
        setMarkReadError(
          apiErrorMessage(err, "Couldn't mark that notification as read.")
        );
      }
    }
  }, [notificationId]);

  useEffect(() => {
    void fetchNotification();
  }, [fetchNotification]);

  const formatDate = (dateString: string | null | undefined) => {
    if (!dateString) return '-';
    try {
      return new Date(dateString).toLocaleString();
    } catch {
      return 'Invalid date';
    }
  };

  const getNotificationIcon = (type: string) => {
    switch (type.toLowerCase()) {
      case 'violation':
        return <ExclamationTriangleIcon className="h-8 w-8 text-red-500" />;
      case 'info':
        return <InformationCircleIcon className="h-8 w-8 text-blue-500" />;
      case 'success':
        return <CheckCircleIcon className="h-8 w-8 text-green-500" />;
      case 'warning':
        return <ExclamationTriangleIcon className="h-8 w-8 text-yellow-500" />;
      default:
        return <BellIcon className="h-8 w-8 text-gray-500" />;
    }
  };

  const getTypeColor = (type: string) => {
    switch (type.toLowerCase()) {
      case 'violation':
        return 'bg-red-100 text-red-800 dark:bg-red-800 dark:text-red-100';
      case 'info':
        return 'bg-blue-100 text-blue-800 dark:bg-blue-800 dark:text-blue-100';
      case 'success':
        return 'bg-green-100 text-green-800 dark:bg-green-800 dark:text-green-100';
      case 'warning':
        return 'bg-yellow-100 text-yellow-800 dark:bg-yellow-800 dark:text-yellow-100';
      default:
        return 'bg-zinc-100 text-zinc-800 dark:bg-zinc-800 dark:text-zinc-100';
    }
  };

  if (loading) {
    return <LoadingSpinner />;
  }

  return (
    <SidebarLayout
      sidebar={<AppSidebar currentPage="notifications" />}
      navbar={<Navbar>{/* Your navbar content */}</Navbar>}
    >
      <div className="flex justify-between items-center mb-6">
        <Heading level={2}>Notification Details</Heading>
      </div>
      <Divider className="mt-4 mb-6" />

      <ErrorAlert className="mb-6" onDismiss={() => setMarkReadError(null)}>
        {markReadError}
      </ErrorAlert>

      {!notification ? (
        <ErrorPage
          title="Couldn't load this notification"
          message={loadError ?? "We couldn't find that notification."}
          onRetry={loadError ? () => void fetchNotification() : undefined}
        />
      ) : (
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          {/* Main Content */}
          <div className="lg:col-span-2 space-y-6">
            {/* Header Card */}
            <div className="bg-slate-100 dark:bg-gray-600 p-6 rounded-lg shadow">
              <div className="flex items-start gap-4">
                {getNotificationIcon(notification.notification_type)}
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-3 mb-2">
                    <h1 className="text-2xl font-bold text-gray-900 dark:text-white truncate">
                      {notification.title}
                    </h1>
                    <span
                      className={`px-3 py-1 text-sm font-semibold rounded-full ${getTypeColor(
                        notification.notification_type
                      )}`}
                    >
                      {notification.notification_type}
                    </span>
                    {!notification.is_read && (
                      <span className="h-3 w-3 bg-sky-600 rounded-full"></span>
                    )}
                  </div>
                  <p className="text-gray-600 dark:text-gray-300 text-sm mb-4">
                    {formatDate(notification.created_at)}
                  </p>
                </div>
              </div>
            </div>

            {/* Message Card */}
            <div className="bg-slate-100 dark:bg-gray-600 p-6 rounded-lg shadow">
              <h3 className="text-lg font-semibold mb-4 text-gray-900 dark:text-white">
                Message
              </h3>
              <p className="text-gray-900 dark:text-white leading-relaxed whitespace-pre-wrap">
                {notification.message}
              </p>
            </div>

            {/* Entity Information (if available) */}
            {(notification.entity_type || notification.entity_id) && (
              <div className="bg-slate-100 dark:bg-gray-600 p-6 rounded-lg shadow">
                <h3 className="text-lg font-semibold mb-4 text-gray-900 dark:text-white">
                  Related Information
                </h3>
                <dl className="space-y-4">
                  {notification.entity_type && (
                    <div className="flex justify-between items-center">
                      <dt className="text-sm font-semibold dark:text-white text-gray-900">
                        Entity Type
                      </dt>
                      <dd className="text-sm text-gray-900 dark:text-white">
                        {notification.entity_type}
                      </dd>
                    </div>
                  )}
                  {notification.entity_id && (
                    <div className="flex justify-between items-center">
                      <dt className="text-sm font-semibold dark:text-white text-gray-900">
                        Entity ID
                      </dt>
                      <dd className="text-sm text-gray-900 dark:text-white">
                        {notification.entity_id}
                      </dd>
                    </div>
                  )}
                </dl>
                {/* A reservation notice is about one booking; take the
                    homeowner to it (#643). */}
                {notification.entity_id &&
                  notification.entity_type?.toLowerCase() === 'reservation' && (
                    <Link
                      to={`/reservations/${notification.entity_id}`}
                      className="mt-4 inline-block text-sm font-medium text-sky-700 hover:underline dark:text-sky-300"
                    >
                      View reservation
                    </Link>
                  )}
                {/* A waitlist offer or a suspension is about the household,
                    not one booking (#640 phase 3). */}
                {notification.entity_type?.toLowerCase() === 'reservation_waitlist' && (
                  <Link
                    to="/reservations#waitlist"
                    className="mt-4 inline-block text-sm font-medium text-sky-700 hover:underline dark:text-sky-300"
                  >
                    View my waitlist
                  </Link>
                )}
                {notification.entity_type?.toLowerCase() === 'amenity_suspension' && (
                  <Link
                    to="/amenities"
                    className="mt-4 inline-block text-sm font-medium text-sky-700 hover:underline dark:text-sky-300"
                  >
                    View amenities
                  </Link>
                )}
              </div>
            )}
          </div>

          {/* Sidebar */}
          <div className="space-y-6">
            {/* Status Information */}
            <div className="bg-slate-100 dark:bg-gray-600 p-6 rounded-lg shadow">
              <h3 className="text-lg font-semibold mb-4 text-gray-900 dark:text-white">
                Status
              </h3>
              <dl className="space-y-4">
                <div className="flex justify-between items-center">
                  <dt className="text-sm font-semibold dark:text-white text-gray-900">
                    Read Status
                  </dt>
                  <dd className="text-sm text-gray-900 dark:text-white">
                    <span
                      className={`px-2 py-1 text-xs font-semibold rounded-full ${
                        notification.is_read
                          ? 'bg-green-100 text-green-800 dark:bg-green-800 dark:text-green-100'
                          : 'bg-yellow-100 text-yellow-800 dark:bg-yellow-800 dark:text-yellow-100'
                      }`}
                    >
                      {notification.is_read ? 'Read' : 'Unread'}
                    </span>
                  </dd>
                </div>
                <div className="flex justify-between items-center">
                  <dt className="text-sm font-semibold dark:text-white text-gray-900">
                    Email Sent
                  </dt>
                  <dd className="text-sm text-gray-900 dark:text-white">
                    <span
                      className={`px-2 py-1 text-xs font-semibold rounded-full ${
                        notification.email_sent
                          ? 'bg-green-100 text-green-800 dark:bg-green-800 dark:text-green-100'
                          : 'bg-gray-100 text-gray-800 dark:bg-gray-800 dark:text-gray-100'
                      }`}
                    >
                      {notification.email_sent ? 'Yes' : 'No'}
                    </span>
                  </dd>
                </div>
              </dl>
            </div>

            {/* Timestamps */}
            <div className="bg-slate-100 dark:bg-gray-600 p-6 rounded-lg shadow">
              <h3 className="text-lg font-semibold mb-4 text-gray-900 dark:text-white">
                Timestamps
              </h3>
              <dl className="space-y-4">
                <div className="flex flex-col gap-1">
                  <dt className="text-sm font-semibold dark:text-white text-gray-900">
                    Created
                  </dt>
                  <dd className="text-sm text-gray-900 dark:text-white">
                    {formatDate(notification.created_at)}
                  </dd>
                </div>
                <div className="flex flex-col gap-1">
                  <dt className="text-sm font-semibold dark:text-white text-gray-900">
                    Last Updated
                  </dt>
                  <dd className="text-sm text-gray-900 dark:text-white">
                    {formatDate(notification.updated_at)}
                  </dd>
                </div>
              </dl>
            </div>

            {/* Additional Details */}
            <div className="bg-slate-100 dark:bg-gray-600 p-6 rounded-lg shadow">
              <h3 className="text-lg font-semibold mb-4 text-gray-900 dark:text-white">
                Details
              </h3>
              <dl className="space-y-4">
                <div className="flex justify-between items-center">
                  <dt className="text-sm font-semibold dark:text-white text-gray-900">
                    Notification ID
                  </dt>
                  <dd className="text-sm text-gray-900 dark:text-white">
                    {notification.id}
                  </dd>
                </div>
                <div className="flex justify-between items-center">
                  <dt className="text-sm font-semibold dark:text-white text-gray-900">
                    HOA ID
                  </dt>
                  <dd className="text-sm text-gray-900 dark:text-white">
                    {notification.hoa_id}
                  </dd>
                </div>
                {notification.user_id && (
                  <div className="flex justify-between items-center">
                    <dt className="text-sm font-semibold dark:text-white text-gray-900">
                      User ID
                    </dt>
                    <dd className="text-sm text-gray-900 dark:text-white">
                      {notification.user_id}
                    </dd>
                  </div>
                )}
                {notification.member_id && (
                  <div className="flex justify-between items-center">
                    <dt className="text-sm font-semibold dark:text-white text-gray-900">
                      Member ID
                    </dt>
                    <dd className="text-sm text-gray-900 dark:text-white">
                      {notification.member_id}
                    </dd>
                  </div>
                )}
              </dl>
            </div>
          </div>
        </div>
      )}
    </SidebarLayout>
  );
};

export default NotificationDetailPage;
